import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Paper,
  Fade,
  Skeleton,
  useTheme,
  alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { toast } from 'react-toastify';
import ProductCategoryChip from './ProductCategoryChip';

// Styled components for enhanced beauty
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.secondary.main, 0.02)})`,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  maxWidth: '100%',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    opacity: 0.6,
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
  }
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(6),
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
  borderRadius: theme.spacing(2),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '200px',
    height: '200px',
    background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
    animation: 'pulse 2s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'translate(-50%, -50%) scale(0.8)',
      opacity: 0.5,
    },
    '50%': {
      transform: 'translate(-50%, -50%) scale(1.2)',
      opacity: 0.2,
    },
    '100%': {
      transform: 'translate(-50%, -50%) scale(0.8)',
      opacity: 0.5,
    },
  },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: '#000000',
  fontWeight: 600,
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-4px',
    left: 0,
    width: '40px',
    height: '3px',
    background: '#000000',
    borderRadius: '2px',
  }
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.5)}, ${alpha(theme.palette.grey[50], 0.8)})`,
  borderRadius: theme.spacing(1.5),
  border: `2px dashed ${alpha(theme.palette.grey[400], 0.3)}`,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '10px',
    left: '10px',
    right: '10px',
    bottom: '10px',
    borderRadius: theme.spacing(1),
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.secondary.main, 0.02)})`,
  }
}));

const CategoryGrid = styled(Grid)(({ theme }) => ({
  width: '100%',
  margin: 0,
  '& .MuiGrid-container': {
    width: '100%',
    margin: 0,
  },
  '& .MuiGrid-item': {
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    paddingLeft: theme.spacing(1),
    paddingTop: theme.spacing(1),
    maxWidth: '100%',
    wordBreak: 'break-word',
    '&:hover': {
      transform: 'scale(1.02)',
    }
  }
}));

const ExistingProductCategories = ({ productId, refreshTrigger }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/v1/product-categories/product/${productId}/existing-categories`);
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // toast.error('Failed to fetch categories.');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  const handleDeleteCategory = async (categoryId) => {
    try {
      const confirmDelete = window.confirm('Are you sure you want to delete this category?');
      if (!confirmDelete) return;
      const response = await axios.delete(`/api/v1/product-categories/delete-product-category/${categoryId}`);
      toast.success(response.data.message || 'Category deleted!');
      setCategories((prev) => prev.filter((cat) => cat.product_category_id !== categoryId));
    } catch (err) {
      console.error('Error deleting category:', err);
      toast.error('Failed to delete category.');
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories, refreshTrigger]);

  if (loading) {
    return (
      <Box mt={4}>
        <Box mb={3}>
          <Skeleton 
            variant="text" 
            width={280} 
            height={40} 
            sx={{ 
              borderRadius: 1,
              background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
            }} 
          />
        </Box>
        <LoadingContainer>
          <CircularProgress 
            size={48}
            thickness={3}
            sx={{
              color: theme.palette.primary.main,
              position: 'relative',
              zIndex: 1,
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              }
            }}
          />
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 2, 
              position: 'relative', 
              zIndex: 1,
              fontWeight: 500,
              color: '#000000'
            }}
          >
            Loading categories...
          </Typography>
        </LoadingContainer>
      </Box>
    );
  }

  return (
    <Fade in timeout={600}>
      <Box mt={4} sx={{ width: '100%', maxWidth: '100%' }}>
        <Box mb={3}>
          <StyledTypography variant="h6" gutterBottom>
            Existing Product Categories
          </StyledTypography>
        </Box>

        <StyledPaper elevation={0}>
          {categories.length === 0 ? (
            <Fade in timeout={800}>
              <EmptyStateContainer>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ 
                    position: 'relative', 
                    zIndex: 1,
                    fontWeight: 500,
                    fontSize: '1.1rem',
                    color: '#000000'
                  }}
                >
                  📦 No Categories Found
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    position: 'relative', 
                    zIndex: 1,
                    opacity: 0.7,
                    color: '#000000'
                  }}
                >
                  No categories have been assigned to this product yet.
                </Typography>
              </EmptyStateContainer>
            </Fade>
          ) : (
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
              <CategoryGrid container spacing={3} sx={{ width: '100%', margin: 0 }}>
                {categories.map((category, index) => (
                  <Fade 
                    in 
                    timeout={600} 
                    style={{ transitionDelay: `${index * 100}ms` }}
                    key={category.product_category_id}
                  >
                    <Grid 
                      item 
                      xs={12} 
                      sm={6} 
                      md={4} 
                      lg={3}
                      sx={{ 
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        maxWidth: '100%',
                        '& > *': {
                          maxWidth: '100%',
                          minWidth: 0,
                          flexShrink: 1,
                        }
                      }}
                    >
                      <Box sx={{ width: '100%', maxWidth: '100%' }}>
                        <ProductCategoryChip
                          category={category}
                          onDelete={handleDeleteCategory}
                        />
                      </Box>
                    </Grid>
                  </Fade>
                ))}
              </CategoryGrid>
            </Box>
          )}
        </StyledPaper>
      </Box>
    </Fade>
  );
};

export default ExistingProductCategories;